import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCustomerData } from '../hooks/useCustomerData';
import PaymentForm from '../components/PaymentForm';
import { Plan } from '../components/PlanSelection';

interface Invoice {
  id: string;
  amount: number;
  status: string;
  date: string;
  plan: string;
  period: string;
}

const plans: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    period: 'per month',
    features: [
      '1,000 URLs per month',
      'Basic analytics',
      'Eddystone optimization',
      'API access'
    ]
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 299,
    period: 'per month',
    features: [
      '50,000 URLs per month',
      'Advanced analytics',
      'Custom domains',
      'Bulk operations',
      'Priority support'
    ]
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 0, // Custom pricing
    period: 'custom',
    features: [
      'Unlimited URLs',
      'White-label solution',
      'Dedicated support',
      'SLA guarantee',
      'Custom integrations'
    ]
  }
];

const Billing: React.FC = () => {
  const { getIdToken } = useAuth();
  const { profile, loading, refreshData } = useCustomerData();
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [changingPlan, setChangingPlan] = useState(false);

  useEffect(() => {
    if (profile) {
      fetchInvoices();
    }
  }, [profile]);

  const fetchInvoices = async () => {
    setLoadingInvoices(true);
    try {
      const token = await getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/invoices`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Ensure data is always an array
        setInvoices(Array.isArray(data) ? data : []);
      } else {
        // Set empty array if request fails
        setInvoices([]);
      }
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      // Set empty array on error
      setInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handlePlanUpgrade = (planId: string) => {
    setSelectedPlanId(planId);
    setShowPaymentForm(true);
    setError('');
  };

  const handlePlanDowngrade = async (planId: string) => {
    if (!window.confirm('Are you sure you want to downgrade your plan? This will take effect at the end of your current billing period.')) {
      return;
    }

    setChangingPlan(true);
    try {
      const token = await getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/change-plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          plan: planId,
        }),
      });

      if (response.ok) {
        setSuccess('Plan change scheduled successfully!');
        refreshData();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to change plan');
      }
    } catch (error) {
      setError('Failed to change plan');
    } finally {
      setChangingPlan(false);
    }
  };

  const handlePaymentSuccess = (subscriptionId: string) => {
    setSuccess('Subscription updated successfully!');
    setShowPaymentForm(false);
    setSelectedPlanId('');
    refreshData();
  };

  const handlePaymentError = (error: string) => {
    setError(error);
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.')) {
      return;
    }

    setChangingPlan(true);
    try {
      const token = await getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setSuccess('Subscription cancelled successfully. You will retain access until the end of your billing period.');
        refreshData();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to cancel subscription');
      }
    } catch (error) {
      setError('Failed to cancel subscription');
    } finally {
      setChangingPlan(false);
    }
  };

  const selectedPlan = plans.find(plan => plan.id === selectedPlanId);
  const currentPlan = plans.find(plan => plan.id === profile?.plan) || plans[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Billing & Plans</h1>
        <p className="mt-2 text-sm text-gray-700">
          Manage your subscription and billing information
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-macos text-sm mb-6">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-macos text-sm mb-6">
          {success}
        </div>
      )}

      {/* Payment Form Modal */}
      {showPaymentForm && selectedPlan && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-macos bg-white">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Upgrade to {selectedPlan.name} Plan
              </h3>
              <button
                onClick={() => {
                  setShowPaymentForm(false);
                  setSelectedPlanId('');
                  setError('');
                }}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <PaymentForm
              selectedPlan={selectedPlan}
              onPaymentSuccess={handlePaymentSuccess}
              onPaymentError={handlePaymentError}
              loading={changingPlan}
              setLoading={setChangingPlan}
            />
          </div>
        </div>
      )}

      {/* Current Plan Status */}
      <div className="mb-8">
        <div className="bg-white shadow-macos rounded-macos-lg border border-gray-100">
          <div className="px-6 py-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Current Plan</h3>
                <div className="mt-2 flex items-center space-x-4">
                  <span className="text-2xl font-bold text-gray-900 capitalize">
                    {profile?.plan || 'Free'}
                  </span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {profile?.billing_status || 'Active'}
                  </span>
                </div>
                {profile?.usage && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                      <span>Usage this month</span>
                      <span>
                        {profile.usage.current_month} / {profile.usage.limit === -1 ? '∞' : profile.usage.limit}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${profile.usage.limit === -1 ? 0 : Math.min((profile.usage.current_month / profile.usage.limit) * 100, 100)}%`
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
              {profile?.plan !== 'free' && (
                <button
                  onClick={handleCancelSubscription}
                  disabled={changingPlan}
                  className="px-4 py-2 border border-red-300 text-sm font-medium rounded-macos text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {changingPlan ? 'Processing...' : 'Cancel Subscription'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Available Plans */}
      <div className="mb-8">
        <h2 className="text-lg font-medium text-gray-900 mb-6">Available Plans</h2>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.isArray(plans) && plans.map((plan) => {
            const isCurrent = plan.id === profile?.plan;
            const isUpgrade = plans.findIndex(p => p.id === profile?.plan) < plans.findIndex(p => p.id === plan.id);

            return (
              <div
                key={plan.id}
                className={`bg-white rounded-macos-lg shadow-macos border-2 ${isCurrent ? 'border-primary-500' : 'border-gray-200'
                  } transition-all hover:shadow-macos-lg flex flex-col h-full`}
              >
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-lg font-medium text-gray-900">{plan.name}</h3>
                  <div className="mt-4">
                    <span className="text-3xl font-bold text-gray-900">
                      {plan.id === 'enterprise' ? 'Custom' : `R${plan.price}`}
                    </span>
                    {plan.id !== 'enterprise' && (
                      <span className="text-sm text-gray-500">/{plan.period}</span>
                    )}
                  </div>
                  <ul className="mt-6 space-y-3 flex-grow">
                    {Array.isArray(plan.features) && plan.features.map((feature) => (
                      <li key={feature} className="flex items-start">
                        <svg
                          className="flex-shrink-0 h-5 w-5 text-green-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="ml-3 text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6">
                    {isCurrent ? (
                      <button
                        disabled
                        className="w-full bg-gray-100 text-gray-400 py-3 px-4 rounded-macos text-sm font-medium cursor-not-allowed"
                      >
                        Current Plan
                      </button>
                    ) : plan.id === 'enterprise' ? (
                      <a
                        href="mailto:sales@rfdigi.co.za"
                        className="w-full inline-flex items-center justify-center bg-gray-500 hover:bg-gray-600 text-white py-3 px-4 rounded-macos text-sm font-medium transition-colors shadow-sm"
                      >
                        Contact Sales
                      </a>
                    ) : isUpgrade ? (
                      <button
                        onClick={() => handlePlanUpgrade(plan.id)}
                        disabled={changingPlan}
                        className="w-full bg-primary-500 hover:bg-primary-600 text-white py-3 px-4 rounded-macos text-sm font-medium transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {changingPlan ? 'Processing...' : 'Upgrade'}
                      </button>
                    ) : (
                      <button
                        onClick={() => handlePlanDowngrade(plan.id)}
                        disabled={changingPlan}
                        className="w-full bg-gray-500 hover:bg-gray-600 text-white py-3 px-4 rounded-macos text-sm font-medium transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {changingPlan ? 'Processing...' : 'Downgrade'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Invoice History */}
      <div className="bg-white rounded-macos-lg shadow-macos border border-gray-100">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Invoice History</h3>
        </div>
        <div className="p-6">
          {loadingInvoices ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-8">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No invoices yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Your invoice history will appear here once you subscribe to a paid plan.
              </p>
            </div>
          ) : (
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array.isArray(invoices) && invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{invoice.id.slice(-8)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                        {invoice.plan}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${invoice.amount.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                          invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                          {invoice.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(invoice.date).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Billing;